// schema.ts
import { z } from 'zod/v4';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { getCountry } from '@helper/getConfigValues';

type Opts = {
    isEngagementVenue: boolean;
    hideGuest: boolean;
};

const dateIsInFuture = (_date: Date, _compareTo: Date = new Date()) => {
    const date = new Date(_date);
    date.setHours(0, 0, 0, 0);
    const compareTo = new Date(_compareTo);
    compareTo.setHours(0, 0, 0, 0);
    return date > compareTo;
}

export const makeSchema = (opts: Opts) => {
    const {
        isEngagementVenue,
        hideGuest,
    } = opts;

    /* ---------- name, email, phone ---------- */

    const phoneSchema = z.string().check((ctx) => {
        const value = ctx.value;
        let errorMessage = '';
        const countryCode = getCountry();
        if (!value) {
            errorMessage = "Phone is required";
        } else if (countryCode === 'au') {
            errorMessage = isValidPhoneNumber(value, 'AU') ? '' : "Enter a valid Australian phone number";
        } else if (countryCode === 'uk') {
            errorMessage = isValidPhoneNumber(value, 'GB') ? '' : "Enter a valid UK phone number";
        } else if (!isValidPhoneNumber(value)) {
            errorMessage = "Enter a valid phone number";
        }
        if (errorMessage) {
            ctx.issues.push({
                message: errorMessage,
                code: z.ZodIssueCode.custom,
                input: value,
            });
        }
    });

    let base = z.object({
        name: z.string().min(1, 'Enter your full name'),
        email: z.email('Invalid email address')
            .min(1, 'Email is required'),
        phone: phoneSchema,
        message: z.string().min(1, 'Message is required'),
        /* ---------- dates (engagement / wedding) ---------- */
        engagementDate: z.preprocess(
            (v) => v === '' ? undefined : v,
            z.coerce.date().optional(),
        ).refine((v) => !isEngagementVenue || (v && dateIsInFuture(v)), {
            message: 'Please enter a date in the future',
        }),
        date: z.preprocess(
            (v) => v === '' ? undefined : v,
            z.coerce.date({ message: 'Please enter a date in the future' }),
        ).refine((v) => v ? dateIsInFuture(v) : false, {
            message: 'Please enter a date in the future',
        })
    });

    const guestsSchema = z.preprocess(
        (v: number | '') => v === '' ? undefined : Number(v), // Allow to use empty string, but don't allow to be it valid
        z.coerce.number().int().optional()
    ).prefault('').check((ctx) => {
        const { value: _value } = ctx;
        if ( hideGuest ) return;
        const value = Number(_value);
        if( value < 0 || isNaN(value)) {
            ctx.issues.push({
                message: 'Min 0 guests',
                code: z.ZodIssueCode.custom,
                input: value,
            });
        }
        if( value > 10000) {
            ctx.issues.push({
                message: 'Max 10000 guests',
                code: z.ZodIssueCode.custom,
                input: value,
            });
        }
    });

    const baseWithGuests = base.extend({
        guests: guestsSchema,
    });

    /* ---------- venue-section ---------- */

    const venueSchema = z.object({
        haveVenue: z.literal('Yes'),
        venueName: z.string().optional(),
        venueId: z.number().or(z.string()).optional(),
        regionId: z.string().or(z.number()).optional(),
        regionName: z.string().optional(),
    })
        .check((ctx) => {
            const { venueName, venueId } = ctx.value;
            if (!venueName || !venueId) {
                ctx.issues.push({
                    message: 'Select a venue',
                    code: z.ZodIssueCode.custom,
                    input: venueId,
                });
            }
        });
    const venueLocationSchema = z.object({
        haveVenue: z.literal('No'),
        venueName: z.string().optional(),
        venueId: z.number().or(z.string()).optional(),
        regionId: z.string().or(z.number()).optional(),
        regionName: z.string().optional(),
    })
        .check((ctx) => {
            const { regionId, regionName } = ctx.value;
            if (!regionId || !regionName) {
                ctx.issues.push({
                    message: 'Select a location',
                    code: z.ZodIssueCode.custom,
                    input: regionId,
                });
            }
        });
    // If user ignore this option, we should not validate venue or region fields
    const noInfoVenueSchema = z.object({
        haveVenue: z.literal('').optional(),
        venueName: z.string().optional(),
        venueId: z.string().optional(),
        regionId: z.string().optional(),
        regionName: z.string().optional(),
    });

    const weddingLocationSchema = z.discriminatedUnion('haveVenue', [
        venueSchema,
        venueLocationSchema,
        noInfoVenueSchema,
    ], {
        message: 'Please select an option',
    })

    const schemaWithVenue = baseWithGuests.extend({
        venue: weddingLocationSchema.transform(v => ({
            ...v,
            // clear opposite option fields when user change option
            [v.haveVenue === 'Yes' ? 'regionId'  : 'venueId' ]: '',
            [v.haveVenue === 'Yes' ? 'regionName': 'venueName']: '',
          })),
    });

    /**
     * Adds a cross-field check to ensure engagementDate is strictly before wedding date
     * when isEngagementVenue is true. Both dates must be present and valid.
     *
     * @param {z.ZodCheckContext} ctx - Zod context containing parsed values.
     * @returns {void}
     */
    const finalSchema = schemaWithVenue.check((ctx) => {
        const { engagementDate, date } = ctx.value;
        if (
            isEngagementVenue &&
            engagementDate instanceof Date &&
            date instanceof Date &&
            !dateIsInFuture(date, engagementDate)
        ) {
            ctx.issues.push({
                path: ['engagementDate'],
                message: 'Engagement date must be before wedding date',
                code: z.ZodIssueCode.custom,
                input: engagementDate,
            });
            ctx.issues.push({
                path: ['date'],
                message: 'Wedding date must be after engagement date',
                code: z.ZodIssueCode.custom,
                input: date,
            });
        }
    });

    return finalSchema;
};

export type FormValidationType = z.infer<ReturnType<typeof makeSchema>>;
export type FormValidationTypeDefaultValues = z.input<ReturnType<typeof makeSchema>>;